import React from "react";
import { Loader2, Inbox, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, children, testId }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-7" data-testid={testId}>
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "indigo", hint, testId }) {
  const accents = {
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    sky: "bg-sky-50 text-sky-600",
    rose: "bg-rose-50 text-rose-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md animate-in-up" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
          <p className="mt-2 font-display text-2xl font-bold text-slate-900">{value}</p>
          {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
        </div>
        {Icon && (
          <div className={cn("flex h-11 w-11 items-center justify-center rounded-lg", accents[accent])}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_MAP = {
  Baru: "bg-blue-50 text-blue-700 border-blue-200",
  Diproses: "bg-amber-50 text-amber-700 border-amber-200",
  "Siap Dikirim": "bg-violet-50 text-violet-700 border-violet-200",
  Dikirim: "bg-sky-50 text-sky-700 border-sky-200",
  Selesai: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Dibatalkan: "bg-rose-50 text-rose-700 border-rose-200",
  Return: "bg-orange-50 text-orange-700 border-orange-200",
  "Retur Sebagian": "bg-orange-50 text-orange-700 border-orange-200",
  "Retur Penuh": "bg-orange-100 text-orange-800 border-orange-300",
  "Tidak Ada Retur": "bg-slate-100 text-slate-500 border-slate-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  inactive: "bg-slate-100 text-slate-500 border-slate-200",
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  Disetujui: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Menunggu Persetujuan": "bg-amber-50 text-amber-700 border-amber-200",
  "Dalam Pengiriman": "bg-sky-50 text-sky-700 border-sky-200",
  Diterima: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Proses Produksi": "bg-indigo-50 text-indigo-700 border-indigo-200",
  Ditolak: "bg-rose-50 text-rose-700 border-rose-200",
  Dikeluarkan: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Dibayar Sebagian": "bg-amber-50 text-amber-700 border-amber-200",
  Lunas: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Tercatat: "bg-sky-50 text-sky-700 border-sky-200",
  Posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Reversed: "bg-rose-50 text-rose-700 border-rose-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  "Belum Jatuh Tempo": "bg-slate-100 text-slate-600 border-slate-200",
  Outstanding: "bg-amber-50 text-amber-700 border-amber-200",
  "Settled Partial": "bg-sky-50 text-sky-700 border-sky-200",
  Settled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "0-7 Hari": "bg-slate-100 text-slate-600 border-slate-200",
  "8-14 Hari": "bg-amber-50 text-amber-700 border-amber-200",
  "15-30 Hari": "bg-orange-50 text-orange-700 border-orange-200",
  "> 30 Hari": "bg-rose-50 text-rose-700 border-rose-200",
  "1-30 Hari": "bg-amber-50 text-amber-700 border-amber-200",
  "31-60 Hari": "bg-orange-50 text-orange-700 border-orange-200",
  "61-90 Hari": "bg-rose-50 text-rose-700 border-rose-200",
  "> 90 Hari": "bg-rose-100 text-rose-800 border-rose-300",
  LOW: "bg-amber-50 text-amber-700 border-amber-200",
  OUT: "bg-rose-50 text-rose-700 border-rose-200",
  OK: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export function StatusBadge({ status, label }) {
  const cls = STATUS_MAP[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold", cls)}>
      {label || status}
    </span>
  );
}

export function Loading({ label = "Memuat..." }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-slate-400">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      <p className="mt-3 text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({ title = "Belum ada data", subtitle, icon: Icon = Inbox, children }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
        <Icon className="h-7 w-7 text-slate-400" />
      </div>
      <p className="mt-4 font-display text-base font-semibold text-slate-700">{title}</p>
      {subtitle && <p className="mt-1 text-sm text-slate-400 max-w-sm">{subtitle}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function ErrorState({ message }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
      <AlertTriangle className="h-5 w-5" /> {message}
    </div>
  );
}
